// Every Cloudish instance size and what it costs, then the cheapest
// comparable option at each alternative, side by side. Our rows are
// computed from the live rates (GET /pricing.json, same as
// PricingCalculator), so they can't drift from what's actually billed;
// competitor rows are hand-checked against each provider's own pricing
// page and dated, since nobody publishes those as an API.

import { INSTANCE_SIZES, costPerSecond, usePricing } from '../pricing';

const HOURS_PER_MONTH = 730;
// "1 hour a day" includes the 15 minutes an app keeps running after each use
// before it sleeps (the default sleepAfterMinutes),
// the same assumption as /instances.md's "What a year costs by usage".
const LIGHT_USE_HOURS_PER_MONTH = (365 * 1.25) / 12;

export const COMPETITORS_CHECKED = 'September 2026';

interface Competitor {
  provider: string;
  option: string;
  cpu: string;
  memory: string;
  alwaysOn: string;
  lightUse: string;
  source: string;
}

// The cheapest option at each provider that can serve a small web app, US
// regions, list prices before any free tier or credits. "1 hour a day" is an
// app that gets traffic about an hour a day, left to each provider's own
// idle behavior (no start/stop scheduling of your own): anything that
// doesn't scale to zero on its own costs the same as always on.
//   Vercel   — container images (Dockerfile.vercel, Beta) run as Vercel
//              Functions: smallest size 1 vCPU / 2 GB, $0.128 per active
//              CPU-hour + $0.0106 per GB-hour of memory while a request is
//              in flight (iad1), scaled down after 5 idle minutes, no disk.
//              Pro is $20/mo including $20 of usage credit. Busy all month:
//              2 GB × 730 h × $0.0106 = $15.48 of memory, inside the credit,
//              then active CPU on top (left out of the cell: it depends on
//              load, and container images are still Beta). 1 hour a day:
//              ~$0.64 of memory plus a little CPU, also inside the credit.
//   Fargate  — 0.25 vCPU × $0.04048 + 0.5 GB × $0.004445 per hour
//              (us-east-1 x86) × 730 = $9.01, + $3.65 for a public IPv4.
//   OVHcloud — VPS-1's advertised "starting at" price; 40 GB NVMe included.
//   Render   — Starter doesn't sleep (only the Free tier does); disks are
//              $0.25 per GB-month on top.
//   Railway  — billed per second on actual use: $0.000463 per vCPU-minute,
//              $0.000231 per GB-minute. At 0.5 vCPU / 1 GB, fully
//              used: $0.02775/h × 730 = $20.26. The $5 Hobby fee includes $5
//              of usage. 1 hour a day assumes its opt-in Serverless setting,
//              which sleeps a service 5–10 min after its last traffic:
//              ~35.5 h × $0.02775 = $0.98, inside the $5. Volumes $0.15 per
//              GB-month.
//   Fly.io   — shared-cpu-1x / 1 GB in iad: $0.00000075 per shared vCPU-s +
//              (1 − 0.25 GB included) × $0.00000193 per GB-s = $0.0000021975/s,
//              $0.00791/h × 730 = $5.77. New apps stop idle Machines by
//              default (auto_stop_machines = "stop"), and stopped Machines
//              aren't billed for CPU/RAM, only $0.15 per GB of rootfs per 30
//              days: 1 hour a day ≈ 35.5 h × $0.00791 = $0.28, + ~$0.15 for a
//              1 GB image stopped the rest of the month = $0.43.
const COMPETITORS: Competitor[] = [
  {
    provider: 'Vercel',
    option: 'Pro plan, container images (Beta)',
    cpu: '1',
    memory: '2 GB',
    alwaysOn: '$20.00',
    lightUse: '$20.00',
    source: 'https://vercel.com/docs/functions/container-images',
  },
  {
    provider: 'AWS',
    option: 'Fargate, smallest task',
    cpu: '0.25',
    memory: '0.5 GB',
    alwaysOn: '$12.66',
    lightUse: '$12.66',
    source: 'https://aws.amazon.com/fargate/pricing/',
  },
  {
    provider: 'OVHcloud',
    option: 'VPS-1',
    cpu: '2',
    memory: '4 GB',
    alwaysOn: '$4.54',
    lightUse: '$4.54',
    source: 'https://us.ovhcloud.com/vps/',
  },
  {
    provider: 'Render',
    option: 'Starter web service',
    cpu: '0.5',
    memory: '0.5 GB',
    alwaysOn: '$7.00',
    lightUse: '$7.00',
    source: 'https://render.com/pricing',
  },
  {
    provider: 'Railway',
    option: 'Hobby plan',
    cpu: '0.5',
    memory: '1 GB',
    alwaysOn: '$20.26',
    lightUse: '$5.00',
    source: 'https://railway.com/pricing',
  },
  {
    provider: 'Fly.io',
    option: 'shared-cpu-1x',
    cpu: '1',
    memory: '1 GB',
    alwaysOn: '$5.77',
    lightUse: '$0.43',
    source: 'https://docs.fly.io/about/pricing/',
  },
];

function usd(n: number, digits = 2): string {
  return `$${n.toFixed(digits)}`;
}

// shared is listed at what it can burst to; the fine print under each table
// explains that it's billed for, and guaranteed, less.
function resourceLabel(base: number, burst: number | undefined, unit = ''): string {
  return `${burst ?? base}${unit}`;
}

export function InstancePricing() {
  const { pricing, error } = usePricing();
  if (error || !pricing) return null; // PricingCalculator above already shows the loading state.

  const rows = INSTANCE_SIZES.map((size) => {
    const perHour = costPerSecond(pricing, size.cpuCores, size.memoryGb) * 3600;
    return { ...size, perHour };
  });
  const cheapest = rows[0];

  return (
    <div className="instance-pricing">
      <p className="faint small when-table-label">Every Cloudish size</p>
      <div className="compare-table-wrap">
        <table className="compare-table">
          <thead>
            <tr>
              <th>Size</th>
              <th>Fully reserved</th>
              <th>CPU</th>
              <th>Memory</th>
              <th>Per hour</th>
              <th>Always on, per month</th>
              <th>1 hour a day, per month</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <th>
                  <code>{r.name}</code>
                </th>
                {/* A size that can burst only reserves its base (see the note below). */}
                <td data-label="Fully reserved">{r.burst ? 'No' : 'Yes'}</td>
                <td data-label="CPU">{resourceLabel(r.cpuCores, r.burst?.cpuCores)}</td>
                <td data-label="Memory">{resourceLabel(r.memoryGb, r.burst?.memoryGb, ' GB')}</td>
                <td data-label="Per hour">{usd(r.perHour, 4)}</td>
                <td data-label="Always on, per month">{usd(r.perHour * HOURS_PER_MONTH)}</td>
                <td data-label="1 hour a day, per month">{usd(r.perHour * LIGHT_USE_HOURS_PER_MONTH)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="faint small instance-pricing-note">
        <code>shared</code> ranges from 0.1 to 1 CPU and from 0.2 to 1 GB of memory: it is
        billed for, and always has, 0.1 CPU and 0.2 GB, and uses up to 1 CPU and 1 GB whenever
        the machine it runs on has them spare. When the machine is busy it runs on its reserved
        0.1 CPU, and if it's using more than 0.2 GB when memory runs short it can be restarted.
        Every other size is fully reserved. Billed per second, only
        while the container runs: an always-on app around the clock, any other until it sleeps,
        15 minutes without requests unless it chooses its own time. Persistent
        volumes are not billed. Full details in <a href="https://cloudish.ai/instances.md">instances.md</a>.
      </p>

      <p className="faint small when-table-label">The cheapest option at each provider</p>
      <div className="compare-table-wrap">
        <table className="compare-table">
          <thead>
            <tr>
              <th>Option</th>
              <th>CPU</th>
              <th>Memory</th>
              <th>Always on, per month</th>
              <th>1 hour a day, per month</th>
            </tr>
          </thead>
          <tbody>
            <tr className="instance-pricing-ours">
              <th>
                Cloudish <span className="instance-pricing-option">{cheapest.name}</span>
              </th>
              <td data-label="CPU">{resourceLabel(cheapest.cpuCores, cheapest.burst?.cpuCores)}</td>
              <td data-label="Memory">{resourceLabel(cheapest.memoryGb, cheapest.burst?.memoryGb, ' GB')}</td>
              <td data-label="Always on, per month">{usd(cheapest.perHour * HOURS_PER_MONTH)}</td>
              <td data-label="1 hour a day, per month">{usd(cheapest.perHour * LIGHT_USE_HOURS_PER_MONTH)}</td>
            </tr>
            {COMPETITORS.map((c) => (
              <tr key={`${c.provider} ${c.option}`}>
                <th>
                  <a href={c.source} target="_blank" rel="noreferrer">
                    {c.provider}
                  </a>{' '}
                  <span className="instance-pricing-option">{c.option}</span>
                </th>
                <td data-label="CPU">{c.cpu}</td>
                <td data-label="Memory">{c.memory}</td>
                <td data-label="Always on, per month">{c.alwaysOn}</td>
                <td data-label="1 hour a day, per month">{c.lightUse}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="faint small instance-pricing-note">
        Cloudish <code>shared</code> ranges from 0.1 to 1 vCPU and from 0.2 to 1 GB of memory,
        as above. List prices in US regions from each provider's own pricing page, checked{' '}
        {COMPETITORS_CHECKED}, before free tiers; OVHcloud's is its advertised starting price.
        "1 hour a day" is an app that gets traffic about an hour a day, left to each provider's own
        idle behavior: Cloudish, Vercel, and Fly.io scale to zero on their own, Railway does once you turn on
        its Serverless setting, and the rest cost the same either way. Railway bills what the app
        actually uses, so one that stays well under 0.5 vCPU and 1 GB costs less, down to its
        $5 minimum. Fly.io's CPU is shared, like <code>shared</code>'s, and a stopped Fly Machine
        still pays about $0.15 a month to store a 1 GB image. Vercel's $20 Pro plan
        includes $20 of usage, which covers its memory ($15.48 for 2 GB busy all month); CPU is
        billed on top at $0.128 per active CPU-hour. OVHcloud is a virtual machine, the
        rest run containers; a VM is cheaper for something that runs all month and that you are
        happy to administer yourself, and comes with its own disk. Vercel has no disk, Fargate
        needs EFS billed separately, Render, Railway, and Fly.io bill disk per GB, and Cloudish
        doesn't bill it. Cloudish is also the only one here an agent
        can sign up for on its own.
      </p>
    </div>
  );
}
